interface SaveResourceActionBase {
  fileName: string;
  displayName: string;
  description?: string;
}

/** Text the assistant authored in its reply; the card previews it and uploads it on Save. */
export interface TextSaveResourceAction extends SaveResourceActionBase {
  content: string;
  staged?: undefined;
}

export interface StagedFileTokens {
  sha: string;
  salt: string;
}

/** Object-store tokens for bytes a tool already uploaded; the card creates the resource from them on Save. */
export interface StagedFile {
  contentToken: StagedFileTokens;
  propertiesToken: StagedFileTokens;
  size: number;
  mime?: string;
}

/**
 * A document a tool rendered on the MCP side (a Word document today) and staged in the object
 * store without creating a resource, so it goes through the same card as a text file.
 */
export interface StagedSaveResourceAction extends SaveResourceActionBase {
  staged: StagedFile;
  // One line standing in for the content preview the card cannot render for a binary file.
  summary?: string;
  content?: undefined;
}

export type SaveResourceAction = TextSaveResourceAction | StagedSaveResourceAction;

export const SAVE_RESOURCE_LANGUAGE = "istari-save-resource";

// What the assistant may author inline. An incoming staged document is validated against
// STAGED_FILE_ALLOWED_EXTENSIONS instead; once parsed, its extension is fixed (allowedExtensionsFor).
export const SAVE_RESOURCE_ALLOWED_EXTENSIONS: readonly string[] = [
  "md",
  "txt",
  "csv",
  "json",
  "yaml",
  "yml",
  "xml",
  "py",
  "html",
];

export const STAGED_FILE_ALLOWED_EXTENSIONS: readonly string[] = ["docx", "xlsx", "pptx", "pdf"];

export const SAVE_RESOURCE_MAX_CONTENT_BYTES = 1_000_000;

export const SAVE_RESOURCE_MAX_FILE_NAME_LENGTH = 128;

/**
 * Instructions appended to the chat system prompt so the assistant emits
 * save-file proposals as fenced blocks matching `parseSaveResourceAction`.
 */
export const SAVE_RESOURCE_INSTRUCTIONS = `# Saving Files
When the user explicitly asks you to save, export, or write conversation content to a file, emit a fenced code block with the language \`${SAVE_RESOURCE_LANGUAGE}\` whose body is a single JSON object:

\`\`\`${SAVE_RESOURCE_LANGUAGE}
{"fileName":"trade-study-summary.md","description":"Optional one-line description","content":"# Trade Study\\n\\nFull file content here."}
\`\`\`

Rules:
- Only emit this block when the user asks for a file; never emit it unprompted.
- "content" must be the complete, final file content as a JSON-escaped string. Never truncate it or use placeholders.
- Do not repeat the file content outside the block; the block renders as a save card and the user reviews the content there before anything is written.
- If it is ambiguous which content the user wants saved, ask instead of guessing.
- If the file was already saved from this conversation — it appears in Referenced Resources marked "(saved from this conversation)" — and the user asks to save the same content again, do not emit another block. Tell them it is already saved and link it: [file-name.ext](/resources/<resource_id>). Only emit a new block if the content has changed since that save, and say the result will be a separate new file, not an update to the existing one.
- "fileName" must be a plain name with no directories and one of these extensions: ${SAVE_RESOURCE_ALLOWED_EXTENSIONS.join(", ")}. Pick the extension that matches the content.
- Keep "content" under 1 MB. For anything larger, tell the user it exceeds the chat save limit.
- Binary documents (Word, Excel, PDF) come from tools, not from you. A tool that produces one returns a ready-made \`${SAVE_RESOURCE_LANGUAGE}\` block in a "save_card" field: paste that block verbatim as its own paragraph, exactly once, and do not edit it. It renders as the same save card, where the user picks the name, classification and whether to add the file to the current system before anything is written — so do not offer those choices in prose and do not link the file (it has no resource until the user presses Save).`;

export function parseSaveResourceAction(code: string): SaveResourceAction | null {
  const record = parseJsonObject(code);
  if (record === null || typeof record.fileName !== "string") return null;
  const fileName = record.fileName.trim();
  const staged = record.staged === undefined ? undefined : parseStagedFile(record.staged);
  if (staged === null) return null;
  const allowedExtensions = staged ? STAGED_FILE_ALLOWED_EXTENSIONS : SAVE_RESOURCE_ALLOWED_EXTENSIONS;
  if (!isValidSaveResourceFileName(fileName, allowedExtensions)) return null;

  const base = parseBaseFields(record, fileName);
  if (base === null) return null;

  if (staged) {
    const summary = optionalText(record.summary);
    if (summary === null) return null;
    return { ...base, staged, ...(summary === undefined ? {} : { summary }) };
  }
  const content = parseContent(record.content);
  return content === null ? null : { ...base, content };
}

function parseJsonObject(code: string): Record<string, unknown> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return parsed as Record<string, unknown>;
}

function parseBaseFields(record: Record<string, unknown>, fileName: string): SaveResourceActionBase | null {
  const displayName = optionalText(record.displayName);
  const description = optionalText(record.description);
  if (displayName === null || description === null) return null;
  return {
    fileName,
    displayName: displayName ?? baseNameOf(fileName),
    ...(description === undefined ? {} : { description }),
  };
}

// Absent or blank → undefined; present but not a string → null (the block is malformed).
function optionalText(value: unknown): string | undefined | null {
  if (value === undefined) return undefined;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseContent(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;
  // Cap the encoded size, not the string length: non-ASCII characters expand
  // to multiple UTF-8 bytes, and the byte size is what the upload carries.
  if (new TextEncoder().encode(value).byteLength > SAVE_RESOURCE_MAX_CONTENT_BYTES) return null;
  return value;
}

// The registry's content address: a lowercase SHA-384 hex digest (96 characters) plus the salt the SDK mixed in.
const SHA384_HEX = /^[0-9a-f]{96}$/;
const TOKEN_SALT = /^[A-Za-z0-9._~+/=-]{1,256}$/;

function parseStagedFile(value: unknown): StagedFile | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const contentToken = parseStagedFileTokens(record.contentToken);
  const propertiesToken = parseStagedFileTokens(record.propertiesToken);
  if (!contentToken || !propertiesToken) return null;
  const { size, mime } = record;
  if (typeof size !== "number" || !Number.isInteger(size) || size <= 0) return null;
  if (mime !== undefined && typeof mime !== "string") return null;
  const trimmedMime = mime?.trim();
  return { contentToken, propertiesToken, size, ...(trimmedMime ? { mime: trimmedMime } : {}) };
}

function parseStagedFileTokens(value: unknown): StagedFileTokens | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const { sha, salt } = value as Record<string, unknown>;
  if (typeof sha !== "string" || !SHA384_HEX.test(sha)) return null;
  if (typeof salt !== "string" || !TOKEN_SALT.test(salt)) return null;
  return { sha, salt };
}

export function isValidSaveResourceFileName(
  fileName: string,
  allowedExtensions: readonly string[] = SAVE_RESOURCE_ALLOWED_EXTENSIONS,
): boolean {
  if (fileName.length === 0 || fileName.length > SAVE_RESOURCE_MAX_FILE_NAME_LENGTH) return false;
  const hasPathOrControlChar = [...fileName].some(
    (char) => char === "/" || char === "\\" || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
  );
  if (hasPathOrControlChar) return false;
  const extension = extensionOf(fileName);
  if (!allowedExtensions.includes(extension)) return false;
  return baseNameOf(fileName).length > 0;
}

// A staged file's bytes are fixed, so a rename may change only the stem: its extension stays what the
// tool produced. Authored text may take any text extension.
export function allowedExtensionsFor(action: Pick<SaveResourceAction, "staged" | "fileName">): readonly string[] {
  return action.staged ? [extensionOf(action.fileName)] : SAVE_RESOURCE_ALLOWED_EXTENSIONS;
}

export function extensionOf(fileName: string): string {
  const separator = fileName.lastIndexOf(".");
  return separator === -1 ? "" : fileName.slice(separator + 1).toLowerCase();
}

export function inferMimeType(fileName: string): string {
  return MIME_BY_EXTENSION[extensionOf(fileName)] ?? "text/plain";
}

export function baseNameOf(fileName: string): string {
  const separator = fileName.lastIndexOf(".");
  return (separator === -1 ? fileName : fileName.slice(0, separator)).trim();
}

const MIME_BY_EXTENSION: Record<string, string> = {
  md: "text/markdown",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  yaml: "application/yaml",
  yml: "application/yaml",
  xml: "application/xml",
  py: "text/x-python",
  html: "text/html",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  pdf: "application/pdf",
};
